import { Content } from 'antd/lib/layout/layout';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { api } from '../../utils/axiosConfig';
import { Notifications } from '../../interfaces/backend/Notifications';
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Input,
  InputRef,
  Modal,
  Popover,
  Row,
  Space,
  Tag
} from 'antd';
import Text from 'antd/lib/typography/Text';
import { Channels } from '../common/channelsEnum';
import {
  BellOutlined,
  ChromeOutlined,
  EditFilled,
  EllipsisOutlined,
  ExclamationCircleFilled,
  MailOutlined,
  MessageOutlined,
  MobileOutlined,
  PhoneOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { blue } from '@ant-design/colors';
import { getChannelShortName } from '../../utils/readable';
import { setAWSCreds } from '../../utils/accessToken';
import { DynamoDB } from 'aws-sdk';
import CloudWatch, { MetricDataResults } from 'aws-sdk/clients/cloudwatch';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import Paragraph from 'antd/lib/typography/Paragraph';
import confirm from 'antd/lib/modal/confirm';
import styled from 'styled-components';
import Search from 'antd/lib/input/Search';
import { Notification } from '../../interfaces/backend/Notification';
import { NotificationPOSTRequest } from '../../interfaces/backend/NotificationPOSTRequest';
import stripDisallowedIdCharacters from '../../utils/stripDisallowedIdCharacters';
import { Template } from '../../interfaces/backend/Template';
import { TemplatesPOSTRequest } from '../../interfaces/backend/TemplatesPOSTRequest';
import { TemplatesPATCHDefaultRequest } from '../../interfaces/backend/TemplatesPATCHDefaultRequest';
import { AxiosError } from 'axios';
import { putNotification } from '../../api/notifications';

const CustomCard = styled(Card)`
  cursor: pointer;
  &:hover {
    border: 1px solid ${blue.primary};
    transition: border 0.3s ease;
  }
  &:hover > .ant-card-actions > li:first-child {
    color: ${blue.primary};
    transition: color 0.3s ease;
  }
`;

export const getChannelIcon = (channel: Channels): React.ReactElement => {
  switch (channel) {
    case Channels.EMAIL:
      return <MailOutlined style={{ color: blue.primary }} />;
    case Channels.SMS:
      return <MessageOutlined style={{ color: blue.primary }} />;
    case Channels.PUSH:
      return <MobileOutlined style={{ color: blue.primary }} />;
    case Channels.CALL:
      return <PhoneOutlined style={{ color: blue.primary }} />;
    case Channels.INAPP_WEB:
      return <BellOutlined style={{ color: blue.primary }} />;
    case Channels.WEB_PUSH:
      return <ChromeOutlined style={{ color: blue.primary }} />;
    default:
      return <MailOutlined style={{ color: blue.primary }} />;
  }
};

const NotificationsContainer = (): React.ReactElement => {
  const history = useHistory();
  const { accountId } = useParams<{ accountId: string }>();
  const [notifications, setNotifications] = useState<Notifications>();
  const [notificationBeingDuplicated, setNotificationBeingDuplicated] =
    useState<Notification>();
  const [duplicationModalOpenState, setDuplicationModalOpenState] =
    useState(false);
  const [duplicationRunning, setDuplicationRunning] = useState(false);
  const [duplicationError, setDuplicationError] = useState('');
  const [newDuplicateNotificationId, setNewDuplicateNotificationId] =
    useState('');
  const [search, setSearch] = useState('');
  const searchInput = useRef<InputRef>(null);
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [chartData, setChartData] = useState<Record<string, number[]>>();

  // notifications:
  useEffect(() => {
    // get notifications with dynamodb query:
    setAWSCreds().then(async () => {
      const dynamodb = new DynamoDB.DocumentClient();
      const res = await dynamodb
        .query({
          TableName: 'notificationsTable5',
          KeyConditionExpression: 'envId = :envId',
          ExpressionAttributeValues: {
            ':envId': accountId
          }
        })
        .promise();
      setNotifications(res.Items as Notifications);
      if (searchInput.current) searchInput.current.focus();
    });
  }, [accountId]);

  // metrics:
  useEffect(() => {
    if (!notifications || notifications.length === 0) return;
    setAWSCreds().then(async () => {
      const metrics = [
        // byNotificationIds
        ...Object.values(notifications).map((notification) => {
          return {
            id: notification.notificationId,
            name: 'byNotificationId',
            dimensions: [
              {
                Name: 'notificationId',
                Value: notification.notificationId
              }
            ]
          };
        })
      ];

      const params: CloudWatch.GetMetricDataInput = {
        MetricDataQueries: metrics.map((m) => {
          return {
            Id: m.id,
            MetricStat: {
              Metric: {
                Namespace: 'Notification Analytics',
                MetricName: `${accountId}-${m.name}`,
                Dimensions: m.dimensions
              },
              Period: 86400,
              Stat: 'SampleCount'
            }
          };
        }),
        EndTime: moment().endOf('day').toDate(),
        StartTime: moment().subtract(1, 'month').startOf('day').toDate()
      };
      const res = await api('POST', `${accountId}/insights`, params);
      const data = res.data as MetricDataResults;

      // chart labels
      const dateToChartFormat = (date: Date): string => {
        return `${date.toLocaleString('default', { month: 'long' })} ${String(
          date.getDate()
        ).padStart(2, '0')}`;
      };
      const labels: string[] = [];
      const currentDate = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setDate(currentDate.getDate() - i);
        labels.push(dateToChartFormat(date));
      }
      setChartLabels(labels);

      const datasets: Record<string, number[]> = {};
      data.forEach((dataset) => {
        const notificationData = Array(30).fill(0);
        dataset.Timestamps?.forEach((timestamp, index) => {
          const date = new Date(timestamp);
          const formattedDate = dateToChartFormat(date);
          const position = labels.indexOf(formattedDate);
          if (position !== -1) {
            notificationData[position] = dataset.Values?.[index];
          }
        });
        datasets[dataset.Id as string] = notificationData;
      });

      setChartData(datasets);
    });
  }, [accountId, notifications]);

  const deleteNotification = async (notificationId: string) => {
    await api('DELETE', `${accountId}/notifications/${notificationId}`);
    setNotifications(
      notifications?.filter((n) => n.notificationId !== notificationId)
    );
  };

  const duplicateNotification = async () => {
    if (!notifications || !notificationBeingDuplicated) return;
    const envId = notificationBeingDuplicated.envId;

    setDuplicationRunning(true);
    const requestBody: NotificationPOSTRequest = {
      notificationId: newDuplicateNotificationId,
      title: notificationBeingDuplicated.title + ' (copy)',
      channels: notificationBeingDuplicated.channels
    };

    api('POST', `/${envId}/notifications`, requestBody)
      .then(async (res) => {
        // now we need to copy all the templates of the original notification into the new notification
        const templatesToCopy: Template[] = [];
        await Promise.all(
          Object.values(Channels).map(async (c) => {
            const templates = (
              await api(
                'GET',
                `/${envId}/notifications/${notificationBeingDuplicated.notificationId}/templates/${c}`
              )
            ).data as Template[];
            templates.map((t) => templatesToCopy.push(t));
          })
        );

        await Promise.all(
          templatesToCopy.map((t) => {
            const request: TemplatesPOSTRequest =
              t.channel === Channels.EMAIL
                ? {
                    ...{
                      ...t,
                      envId: undefined, // remove params that shouldn't go to the request body
                      notificationId: undefined,
                      default: undefined,
                      channel: undefined,
                      isDefaultFor: undefined
                    }
                  }
                : {
                    ...{
                      ...t,
                      envId: undefined, // remove params that shouldn't go to the request body
                      notificationId: undefined,
                      default: undefined,
                      channel: undefined
                    }
                  };
            return api(
              'POST',
              `/${envId}/notifications/${newDuplicateNotificationId}/templates/${t.channel}`,
              convertRequest(request)
            );
          })
        );

        // the first templates that is copied above becomes default, so we have to set default explicitly
        // set default templates
        await Promise.all(
          templatesToCopy
            .filter((c) => c.default)
            .map((t) => {
              const request: TemplatesPATCHDefaultRequest = {
                templateId: t.templateId
              };
              return api(
                'PATCH',
                `/${envId}/notifications/${newDuplicateNotificationId}/defaulttemplate/${t.channel}`,
                request
              );
            })
        );

        setDuplicationRunning(false);
        setNotificationBeingDuplicated(undefined);
        setDuplicationError('');
        setNewDuplicateNotificationId('');
        setNotifications([...notifications, res.data as Notification]);
        setDuplicationModalOpenState(false);
      })
      .catch((e) => {
        setDuplicationRunning(false);
        const error = e as AxiosError;
        if (
          error.response?.data.message &&
          error.response?.data.message.includes('duplicate notificationId')
        )
          setDuplicationError('There is already a notification with this ID.');
        else throw e;
      });
  };

  const updateNotification = async (n: Notification) => {
    await putNotification(n);
    setNotifications(
      notifications?.map((notification) =>
        notification.notificationId === n.notificationId ? n : notification
      )
    );
  };

  const getChannelTagText = (channel: Channels) => {
    const text = getChannelShortName(channel as Channels);
    return text;
  };

  return (
    <Content style={{ padding: '24px' }}>
      {notifications && notifications.length > 0 && (
        <Row gutter={[16, 8]} style={{ maxWidth: 700 }}>
          <Col xs={24} md={12}>
            <Search
              placeholder="Search notifications"
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
              ref={searchInput}
            />
          </Col>
          <Col xs={24} md={12}>
            <Button
              style={{ width: '100%' }}
              type="primary"
              onClick={() => history.push(`/${accountId}/newnotification`)}
            >
              <PlusOutlined />
              Create Notification
            </Button>
          </Col>
        </Row>
      )}

      {notifications && notifications.length === 0 && (
        <Empty
          description={
            <Space direction="vertical">
              <Text>
                Let's build a simple notification together in 5 minutes!
              </Text>
              <Button
                type="primary"
                onClick={() => history.push(`/${accountId}/newnotification`)}
              >
                Create Notification
              </Button>
            </Space>
          }
        />
      )}
      <Row style={{ marginTop: '12px', maxWidth: 700 }} gutter={[16, 16]}>
        {!notifications && (
          <Col span={24}>
            <Card loading={true} />
          </Col>
        )}
        {notifications
          ?.filter(
            (n) =>
              n.title.toLowerCase().includes(search.toLowerCase()) ||
              n.notificationId.toLowerCase().includes(search.toLowerCase())
          )
          .map((notification) => (
            <Col span={24} key={notification.notificationId}>
              <CustomCard
                onClick={(e) => {
                  const el = e.target as HTMLElement;
                  if (
                    el.closest('.ant-card-actions') ||
                    el.closest('.ant-dropdown')
                  ) {
                    return;
                  } else {
                    history.push(
                      `/${accountId}/notifications/${notification.notificationId}/notificationConfig/overview`
                    );
                  }
                }}
                title={
                  <>
                    <Text>{notification.title}</Text>
                    {!notification.enabled && (
                      <Tag style={{ marginLeft: '8px' }} color="orange">
                        Disabled
                      </Tag>
                    )}
                  </>
                }
                actions={[
                  <span
                    onClick={() =>
                      history.push(
                        `/${accountId}/notifications/${notification.notificationId}/notificationConfig/overview`
                      )
                    }
                  >
                    <>
                      <EditFilled /> Edit
                    </>
                  </span>,
                  <Dropdown
                    menu={{
                      items: [
                        {
                          label: 'Duplicate',
                          key: 'duplicate',
                          danger: false,
                          onClick: () => {
                            setNotificationBeingDuplicated(notification);
                            setDuplicationModalOpenState(true);
                          }
                        },
                        {
                          label: (
                            <Popover
                              content={
                                <div style={{ maxWidth: '300px' }}>
                                  <Paragraph>
                                    Disabling a notification causes requests
                                    with this notificationId to be ignored
                                    without any adverse side-effects. Your code
                                    will continue to work as before, but the
                                    API/SDK will return soft warnings.
                                  </Paragraph>
                                </div>
                              }
                              placement="right"
                              overlayStyle={{
                                paddingLeft: '20px'
                              }}
                              mouseEnterDelay={0}
                            >
                              <div>
                                <Text
                                  type={
                                    notification.enabled ? 'warning' : undefined
                                  }
                                >
                                  {notification.enabled ? 'Disable' : 'Enable'}
                                </Text>
                              </div>
                            </Popover>
                          ),
                          key: 'disable',
                          onClick: () => {
                            updateNotification({
                              ...notification,
                              enabled: !notification.enabled
                            });
                          }
                        },
                        {
                          label: (
                            <Popover
                              content={
                                <div style={{ maxWidth: '300px' }}>
                                  <Paragraph>
                                    Deleting a notification cannot be undone. It
                                    will cause all requests with this
                                    notificationId to throw an error. Before
                                    removing a notification, disable it and
                                    review the logs.
                                  </Paragraph>
                                </div>
                              }
                              placement="right"
                              overlayStyle={{
                                paddingLeft: '20px'
                              }}
                              mouseEnterDelay={0}
                            >
                              <div>Delete </div>
                            </Popover>
                          ),
                          key: 'delete',
                          danger: true,
                          onClick: () => {
                            confirm({
                              title: 'Are you sure?',
                              icon: <ExclamationCircleFilled />,
                              okType: 'danger',
                              okText: 'Delete',
                              cancelText: 'Cancel',
                              okCancel: true,
                              content:
                                'If you used this notification in your code, after deletion, your notifications will route through the default notification.',
                              onOk() {
                                return deleteNotification(
                                  notification.notificationId
                                );
                              }
                            });
                          }
                        }
                      ]
                    }}
                    placement="bottom"
                    trigger={['click']}
                  >
                    <EllipsisOutlined />
                  </Dropdown>
                ]}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Row gutter={[16, 8]}>
                      <Col>
                        {notification.channels.map((c) => (
                          <Tag
                            key={c}
                            icon={getChannelIcon(c as Channels)}
                            style={{ marginBottom: 8 }}
                          >
                            {getChannelTagText(c as Channels)}
                          </Tag>
                        ))}
                      </Col>
                      <Col span={24}>
                        <Text type="secondary">ID:</Text>{' '}
                        <Text>{notification.notificationId}</Text>
                      </Col>
                      <Col span={24}>
                        <Text type="secondary">Options:</Text>{' '}
                        {notification.deduplication ? 'Deduplication' : 'none'}
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={24} md={12} style={{ height: '100px' }}>
                    {chartData && (
                      <Line
                        options={{
                          layout: {
                            padding: 10
                          },
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              padding: 20,
                              titleMarginBottom: 10,
                              boxHeight: 0,
                              boxWidth: 0,
                              backgroundColor: 'rgba(0,0,0,0.7)'
                            }
                          },
                          interaction: {
                            mode: 'nearest',
                            axis: 'x',
                            intersect: false
                          },
                          scales: {
                            x: {
                              display: false
                            },
                            yAxes: {
                              display: false
                            }
                          }
                        }}
                        data={{
                          labels: chartLabels,
                          datasets: [
                            {
                              label: 'Number of sent notifications',
                              data: chartData[notification.notificationId],
                              fill: false,
                              borderColor: 'rgb(153, 102, 255)',
                              tension: 0.33, // curvy smooth graph
                              borderWidth: 2,
                              pointRadius: 0
                            }
                          ]
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </CustomCard>
            </Col>
          ))}
      </Row>

      <Modal
        title="Duplicate Notification"
        open={duplicationModalOpenState}
        onCancel={() => {
          setNotificationBeingDuplicated(undefined);
          setDuplicationModalOpenState(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setNotificationBeingDuplicated(undefined);
              setDuplicationModalOpenState(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            loading={duplicationRunning}
            key="submit"
            type="primary"
            onClick={duplicateNotification}
          >
            Duplicate
          </Button>
        ]}
      >
        New Notification ID (cannot be changed later):
        <Input
          value={newDuplicateNotificationId}
          onChange={(e) =>
            setNewDuplicateNotificationId(
              stripDisallowedIdCharacters(e.target.value)
            )
          }
        />
        {duplicationError && (
          <Alert
            style={{ marginTop: '8px' }}
            message={duplicationError}
            type="error"
          />
        )}
      </Modal>
    </Content>
  );
};
function convertRequest(request: TemplatesPOSTRequest): TemplatesPOSTRequest {
  if ('batch' in request) {
    return {
      templateId: request.templateId || 'default',
      instant: request.instant,
      batch: request.batch
    };
  }

  return request;
}
export default NotificationsContainer;
